import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  ShieldCheck,
  Package,
  FileText,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Sparkles,
  Bot,
} from 'lucide-react';
import { toast } from 'sonner';
import complianceService from '../services/complianceService';
import aiService from '../services/aiService';

const COLORS = ['#10b981', '#6366f1', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

// A card that streams AI analysis text token by token
function AIInsightCard({ title, description, streamFn }) {
  const [text, setText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const abortRef = useRef(null);

  const runAnalysis = () => {
    if (abortRef.current) abortRef.current.abort();
    setText('');
    setIsStreaming(true);
    setHasLoaded(false);

    abortRef.current = streamFn(
      (delta) => setText(prev => prev + delta),
      () => { setIsStreaming(false); setHasLoaded(true); },
      (err) => { setIsStreaming(false); toast.error(`AI analysis failed: ${err}`); }
    );
  };

  useEffect(() => {
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-indigo-50 rounded-lg">
              <Bot className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              <CardDescription>{description}</CardDescription>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={runAnalysis}
            disabled={isStreaming}
          >
            {isStreaming
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <Sparkles className="h-4 w-4 mr-2" />}
            {hasLoaded ? 'Re-run' : 'Run Analysis'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {!text && !isStreaming && (
          <div className="text-center py-8 text-muted-foreground">
            <Sparkles className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Click "Run Analysis" to generate AI-powered insights</p>
          </div>
        )}
        {(text || isStreaming) && (
          <div className="bg-muted/30 rounded-lg p-4 text-sm leading-relaxed whitespace-pre-wrap min-h-[100px]">
            {text}
            {isStreaming && (
              <span className="inline-block w-2 h-4 bg-primary ml-0.5 animate-pulse align-middle" />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPage() {
  const [isLoading, setIsLoading] = useState(true);
  const [dashData, setDashData] = useState(null);
  const [compData, setCompData] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [dash, comp] = await Promise.allSettled([
        complianceService.getDashboard(),
        complianceService.getComplianceDashboard(),
      ]);
      if (dash.status === 'fulfilled') setDashData(dash.value);
      if (comp.status === 'fulfilled') setCompData(comp.value);
    } catch {
      toast.error('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Derived chart data ────────────────────────────────────────────────────

  // 1. Compliance trend — from DashboardDto.Trends
  const trendData = (dashData?.trends ?? []).map(t => ({
    month: new Date(t.month).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
    Approved: t.approved,
    Pending: t.pending,
    Rejected: t.rejected,
    Total: t.total,
  }));

  // 2. License & Permit status bar chart
  const licensePermitData = dashData?.licensePermitSummary
    ? [
        {
          name: 'Licenses',
          Active: dashData.licensePermitSummary.activeLicenses,
          Expiring: dashData.licensePermitSummary.expiringLicenses,
          Expired: dashData.licensePermitSummary.expiredLicenses,
        },
        {
          name: 'Permits',
          Active: dashData.licensePermitSummary.activePermits,
          Expiring: dashData.licensePermitSummary.expiringPermits,
          Expired: dashData.licensePermitSummary.expiredPermits,
        },
      ]
    : [];

  // 3. Batch test results pie
  const batchPieData = compData?.batchSummary
    ? [
        { name: 'Passed', value: compData.batchSummary.passedTests, color: '#10b981' },
        { name: 'Failed', value: compData.batchSummary.failedTests, color: '#ef4444' },
        { name: 'Pending', value: compData.batchSummary.pendingTests, color: '#f59e0b' },
      ].filter(d => d.value > 0)
    : [];

  // 4. Document status pie
  const docPieData = dashData?.compliance
    ? [
        { name: 'Approved', value: dashData.compliance.approved, color: '#10b981' },
        { name: 'Pending', value: dashData.compliance.pending, color: '#f59e0b' },
        { name: 'Rejected', value: dashData.compliance.rejected, color: '#ef4444' },
        { name: 'Expiring Soon', value: dashData.compliance.expiringSoon, color: '#6366f1' },
      ].filter(d => d.value > 0)
    : [];

  // 5. Audit summary bar
  const auditData = compData?.auditSummary
    ? [
        { name: 'Total Audits', value: compData.auditSummary.totalAudits },
        { name: 'Violations', value: compData.auditSummary.violations },
        { name: 'Open Actions', value: compData.auditSummary.openCorrectiveActions },
      ]
    : [];

  // 6. Compliance radar (from compliance score breakdown if available)
  const radarData = [
    { metric: 'Compliance Score', score: compData?.complianceScore ?? 0 },
    { metric: 'Batch Pass Rate', score: compData?.batchSummary
        ? Math.round((compData.batchSummary.passedTests / Math.max(compData.batchSummary.totalBatches, 1)) * 100)
        : 0 },
    { metric: 'Doc Approval Rate', score: dashData?.compliance
        ? Math.round((dashData.compliance.approved / Math.max(dashData.compliance.totalDocuments, 1)) * 100)
        : 0 },
    { metric: 'Active Licenses', score: dashData?.licensePermitSummary
        ? Math.min(Math.round((dashData.licensePermitSummary.activeLicenses / Math.max(dashData.licensePermitSummary.activeLicenses + dashData.licensePermitSummary.expiredLicenses, 1)) * 100), 100)
        : 0 },
    { metric: 'No Violations', score: compData?.auditSummary
        ? Math.max(0, 100 - Math.round((compData.auditSummary.violations / Math.max(compData.auditSummary.totalAudits, 1)) * 100))
        : 0 },
    { metric: 'Inventory Health', score: compData?.inventorySummary
        ? Math.max(0, 100 - Math.round((compData.inventorySummary.lowStockBatches / Math.max(compData.inventorySummary.totalInventories, 1)) * 100))
        : 0 },
  ];

  // Summary stat cards
  const statCards = [
    {
      title: 'Compliance Score',
      value: compData?.complianceScore != null ? `${compData.complianceScore}%` : '—',
      icon: ShieldCheck,
      color: 'text-green-600',
      bg: 'bg-green-50',
      sub: 'Overall compliance health',
    },
    {
      title: 'Active Licenses',
      value: dashData?.licensePermitSummary?.activeLicenses ?? '—',
      icon: FileText,
      color: 'text-blue-600',
      bg: 'bg-blue-50',
      sub: `${dashData?.licensePermitSummary?.expiringLicenses ?? 0} expiring soon`,
    },
    {
      title: 'Total Batches',
      value: compData?.batchSummary?.totalBatches ?? '—',
      icon: Package,
      color: 'text-purple-600',
      bg: 'bg-purple-50',
      sub: `${compData?.batchSummary?.passedTests ?? 0} passed tests`,
    },
    {
      title: 'Risk Score',
      value: dashData?.riskScore != null ? dashData.riskScore.toFixed(1) : '—',
      icon: dashData?.riskScore > 5 ? TrendingUp : TrendingDown,
      color: dashData?.riskScore > 5 ? 'text-red-600' : 'text-green-600',
      bg: dashData?.riskScore > 5 ? 'bg-red-50' : 'bg-green-50',
      sub: dashData?.riskLevel ?? 'Risk level',
    },
  ];

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Analytics</h1>
            <p className="text-muted-foreground mt-1">Compliance metrics and AI-powered insights</p>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((card, i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">{card.title}</p>
                    <p className="text-3xl font-bold mt-2">{card.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">{card.sub}</p>
                  </div>
                  <div className={`p-3 ${card.bg} rounded-lg`}>
                    <card.icon className={`h-6 w-6 ${card.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Compliance Trend */}
        {trendData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Compliance Document Trends</CardTitle>
              <CardDescription>Monthly breakdown of document approvals, pending, and rejections</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="gradApproved" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradPending" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradRejected" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="Approved" stroke="#10b981" fill="url(#gradApproved)" strokeWidth={2} />
                  <Area type="monotone" dataKey="Pending" stroke="#f59e0b" fill="url(#gradPending)" strokeWidth={2} />
                  <Area type="monotone" dataKey="Rejected" stroke="#ef4444" fill="url(#gradRejected)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* License/Permit Status + Batch Test Results */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {licensePermitData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>License & Permit Status</CardTitle>
                <CardDescription>Active, expiring, and expired by type</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={licensePermitData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="Active" fill="#10b981" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Expiring" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Expired" fill="#ef4444" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {batchPieData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Batch Test Results</CardTitle>
                <CardDescription>Distribution of pass / fail / pending tests</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={batchPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {batchPieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Document Status + Audit Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {docPieData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Document Status</CardTitle>
                <CardDescription>Compliance documents by status</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={docPieData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                      labelLine={false}
                    >
                      {docPieData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {auditData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Audit Summary</CardTitle>
                <CardDescription>Total audits, violations, and open corrective actions</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={auditData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis type="number" />
                    <YAxis dataKey="name" type="category" width={110} />
                    <Tooltip />
                    <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                      {auditData.map((entry, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Compliance Health Radar */}
        <Card>
          <CardHeader>
            <CardTitle>Compliance Health Radar</CardTitle>
            <CardDescription>Multi-dimensional compliance scoring (0–100)</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <RadarChart data={radarData}>
                <PolarGrid />
                <PolarAngleAxis dataKey="metric" />
                <PolarRadiusAxis angle={90} domain={[0, 100]} />
                <Radar name="Score" dataKey="score" stroke="#10b981" fill="#10b981" fillOpacity={0.5} />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Risks */}
        {dashData?.topRisks?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Top Compliance Risks</CardTitle>
              <CardDescription>Items requiring immediate attention</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashData.topRisks.map((risk, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 border rounded-lg">
                    <AlertTriangle className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                      risk.severity?.toLowerCase() === 'high' ? 'text-red-500' :
                      risk.severity?.toLowerCase() === 'medium' ? 'text-yellow-500' :
                      'text-blue-500'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{risk.title}</p>
                        <Badge
                          className={
                            risk.severity?.toLowerCase() === 'high' ? 'bg-red-100 text-red-800' :
                            risk.severity?.toLowerCase() === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }
                        >
                          {risk.severity}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{risk.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* AI Insights Section */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="h-5 w-5 text-indigo-600" />
            <h2 className="text-xl font-semibold">AI-Powered Insights</h2>
            <Badge className="bg-indigo-100 text-indigo-800">Powered by Claude</Badge>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <AIInsightCard
              title="Compliance Health Analysis"
              description="AI-generated analysis of your overall compliance posture"
              streamFn={(onChunk, onDone, onError) =>
                aiService.streamComplianceHealth(onChunk, onDone, onError)
              }
            />
            <AIInsightCard
              title="Portfolio Intelligence"
              description="Strategic insights across your entire operator portfolio"
              streamFn={(onChunk, onDone, onError) =>
                aiService.streamPortfolio(onChunk, onDone, onError)
              }
            />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
